import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import { LocaleSwitcher } from '../../../../i18n/components/LocaleSwitcher';
import { JOB_PORTAL_KEYS, NS } from '../../../../i18n/constants/keys';

interface IJobPostTableHeaderProps {
  companyLogo?: string | null;
  companyName?: string | null;
  jobCount: number;
  maxPages: number;
}

export const JobPostTableHeader = ({
  companyLogo,
  companyName,
  jobCount,
  maxPages,
}: IJobPostTableHeaderProps) => {
  const { t } = useTranslation(NS.jobPortal);
  const [logoError, setLogoError] = React.useState<boolean>(false);

  return (
    <Paper
      component="header"
      elevation={0}
      sx={{ borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}
    >
      <Box sx={{ mx: 'auto', maxWidth: '80rem', px: { xs: 2, sm: 3, lg: 4 }, py: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) auto' },
            alignItems: { lg: 'center' },
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 56, sm: 'auto' },
                minWidth: 56,
                maxWidth: { xs: 80, sm: 180 },
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                px: 1.5,
                py: 0.75,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {companyLogo && !logoError ? (
                <Box
                  component="img"
                  src={companyLogo}
                  alt={companyName || 'Company logo'}
                  onError={() => setLogoError(true)}
                  sx={{
                    maxHeight: 40,
                    maxWidth: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              ) : (
                <BusinessCenterIcon fontSize="small" color="action" />
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="primary" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
                Careers
              </Typography>
              <Typography variant="h1" noWrap>
                {t(JOB_PORTAL_KEYS.listing.title)}
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mt: 0.5 }}>
                {companyName ? `Explore open roles at ${companyName}` : t(JOB_PORTAL_KEYS.listing.subtitle)}
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <LocaleSwitcher variant="inline" />
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.100', px: 2, py: 1.5 }}>
              <Typography variant="h2">{jobCount}</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t(JOB_PORTAL_KEYS.listing.availableJobs)}
              </Typography>
            </Box>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.100', px: 2, py: 1.5 }}>
              <Typography variant="h2">{maxPages}</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t(JOB_PORTAL_KEYS.listing.pages)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
};
